/**
 * W65 — OpportunityHeader
 * Top band: account name, opportunity name, status, numbers lead, owner info.
 */
import { Link } from "wouter";

// Forecast hex set — canon §6 (2026-06-09) single hex per band.
const FORECAST_HEX: Record<string, string> = {
  Commit:      "#16A34A",
  Upside:      "#16A34A",
  "Best Case": "#EAB308",
  Pipeline:    "#EA580C",
  Omit:        "#DC2626",
};

const STATUS_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  open:          { bg: "#EFEFEE", color: "#2A2A2E", label: "Open" },
  won:           { bg: "#D1FAE5", color: "#065F46", label: "Won" },
  lost:          { bg: "#FEE2E2", color: "#991B1B", label: "Lost" },
  disqualified:  { bg: "#FEF3C7", color: "#92400E", label: "Disqualified" },
  on_hold:       { bg: "#E5E7EB", color: "#374151", label: "On Hold" },
};

function fmtDollars(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}

function daysOpen(createdAt: string): number {
  const d = Math.floor((Date.now() - new Date(createdAt).getTime()) / 86_400_000);
  return Math.max(0, d);
}

function cpName(cp: number): string {
  const names: Record<number, string> = {
    1: "Discovery",
    2: "Qualification",
    3: "Solution Fit",
    4: "Champion",
    5: "Business Case",
    6: "Decision Process",
    7: "Economic Buyer",
    8: "Technical Validation",
    9: "Negotiate",
    10: "Close",
  };
  return names[cp] ?? `CP${cp}`;
}

interface OpportunityHeaderProps {
  opportunity: {
    opportunity_id?: string;
    id?: string;
    account_id: string;
    account_name: string;
    opportunity_name?: string;
    name?: string;
    status: string;
    gross_value: number;
    net_value: number;
    subtotal_discount_pct?: number;
    forecast_bucket: string;
    current_checkpoint: number;
    est_close_date?: string | null;
    created_at: string;
    primary_owner_name?: string | null;
    bdr_user_id?: string | null;
    bdr_name?: string | null;
    override_active?: boolean;
  };
}

export function OpportunityHeader({ opportunity: o }: OpportunityHeaderProps) {
  const statusStyle = STATUS_STYLE[o.status] ?? STATUS_STYLE.open;
  const forecastColor = FORECAST_HEX[o.forecast_bucket] ?? "#6B7280";
  const name = o.opportunity_name ?? o.name ?? "Opportunity";
  const discountPct = Number(o.subtotal_discount_pct ?? 0);

  return (
    <div
      style={{
        background: "#FFFFFF",
        borderBottom: "1px solid #E5E5E7",
        padding: "20px 24px 16px",
      }}
    >
      {/* Breadcrumb */}
      <div style={{ fontSize: 12, color: "#8E8E94", marginBottom: 6 }}>
        <Link
          href={`/account/${o.account_id}`}
          style={{ color: "#8E8E94", textDecoration: "none" }}
        >
          {o.account_name}
        </Link>
        <span style={{ margin: "0 6px" }}>·</span>
        <Link
          href={`/account/${o.account_id}/opportunities`}
          style={{ color: "#8E8E94", textDecoration: "none" }}
        >
          Opportunities
        </Link>
      </div>

      {/* Title row */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 12,
        }}
      >
        <h1 style={{ fontSize: 20, fontWeight: 700, color: "#0B0B0F", margin: 0, flex: 1 }}>
          {name}
        </h1>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "3px 10px",
            borderRadius: 99,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            background: statusStyle.bg,
            color: statusStyle.color,
            whiteSpace: "nowrap",
          }}
        >
          {statusStyle.label}
        </span>
      </div>

      {/* Numbers lead */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "16px 32px",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        {/* Net value */}
        <div>
          <div style={{ fontSize: 26, fontWeight: 800, color: "#0B0B0F", lineHeight: 1 }}>
            {fmtDollars(o.net_value)}
          </div>
          {discountPct > 0 && (
            <div style={{ fontSize: 11, color: "#8E8E94", marginTop: 1 }}>
              net of {discountPct}% deal discount · gross {fmtDollars(o.gross_value)}
            </div>
          )}
          {discountPct === 0 && (
            <div style={{ fontSize: 11, color: "#8E8E94", marginTop: 1 }}>net value</div>
          )}
        </div>

        {/* Forecast bucket */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              display: "inline-flex",
              padding: "4px 10px",
              borderRadius: 5,
              fontSize: 12,
              fontWeight: 700,
              background: forecastColor,
              color: "#FFFFFF",
              whiteSpace: "nowrap",
            }}
          >
            {o.forecast_bucket}
          </span>
          {o.override_active && (
            <span
              style={{
                fontSize: 10,
                color: "#B45309",
                fontWeight: 600,
                background: "#FEF3C7",
                padding: "2px 6px",
                borderRadius: 4,
                border: "1px solid #FCD34D",
              }}
            >
              overridden
            </span>
          )}
        </div>

        {/* CP */}
        <div style={{ fontSize: 13, color: "#525258", fontWeight: 600 }}>
          CP{o.current_checkpoint} — {cpName(o.current_checkpoint)}
        </div>

        {/* Est close */}
        {o.est_close_date && (
          <div style={{ fontSize: 13, color: "#525258" }}>
            Est. close:{" "}
            <strong>
              {new Date(o.est_close_date + "T00:00:00").toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </strong>
          </div>
        )}

        {/* Days open */}
        <div style={{ fontSize: 13, color: "#6B7280" }}>
          {daysOpen(o.created_at)} days open
        </div>
      </div>

      {/* Owner row */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        {o.primary_owner_name && (
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                background: "#0B0B0F",
                color: "#FFFFFF",
                fontSize: 10,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {o.primary_owner_name
                .split(" ")
                .map((w: string) => w[0])
                .join("")
                .slice(0, 2)
                .toUpperCase()}
            </div>
            <span style={{ fontSize: 13, color: "#2A2A2E" }}>{o.primary_owner_name}</span>
            <span
              style={{
                fontSize: 10,
                background: "#EFEFEE",
                color: "#525258",
                padding: "1px 6px",
                borderRadius: 4,
                fontWeight: 600,
              }}
            >
              Primary AE
            </span>
          </div>
        )}

        {o.bdr_name && (
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: "#EA580C",
                color: "#FFFFFF",
                fontSize: 9,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {o.bdr_name
                .split(" ")
                .map((w: string) => w[0])
                .join("")
                .slice(0, 2)
                .toUpperCase()}
            </div>
            <span style={{ fontSize: 12, color: "#525258" }}>{o.bdr_name}</span>
            <span
              style={{
                fontSize: 10,
                background: "#FFF7ED",
                color: "#9A3412",
                padding: "1px 6px",
                borderRadius: 4,
                fontWeight: 600,
                border: "1px solid #FDBA74",
              }}
            >
              BDR Credit
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
